import { useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Boxes, Edit, Plus, Trash2 } from 'lucide-react'

import type {
    ListPackagingMaterialsParams,
    PackagingMaterial,
} from '@/lib/api/packaging-api'
import { PageBackButton } from '@/components/common/PageBackButton'
import { BOMEditDialog } from '@/components/packaging/bom-edit-dialog'
import { ListCard } from '@/components/common/ListCard'
import { MaterialFormDialog } from '@/components/packaging/material-form-dialog'
import { Badge } from '@/components/ui/badge'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { defineListFilters } from '@/components/common/filters/core'
import { textFilter } from '@/components/common/filters/text-filter'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/hooks/use-auth'
import {
    useBOMCounts,
    useDeletePackagingMaterial,
    usePackagingMaterials,
} from '@/hooks/use-packaging-query'
import { useProducts } from '@/hooks/use-product-query'
import { m } from '@/lib/paraglide/messages'

const PERMISSION = 'packaging.read'
const BOM_PER_PAGE = 50

type PackagingTab = 'materials' | 'bom'

/**
 * Hai tab là hai danh sách độc lập nên mỗi tab một khoá tìm kiếm riêng, không
 * giẫm chân nhau. `tab` cũng lên URL — không thì gửi link cho đồng nghiệp là
 * họ luôn rơi về tab đầu, và đổi tab sẽ nuốt mất tham số của tab kia.
 */
const MATERIALS_FILTERS = defineListFilters({
    q: textFilter({
        label: m.packaging_search_label,
        placeholder: m.packaging_search_placeholder,
    }),
})

const BOM_FILTERS = defineListFilters({
    bom_q: textFilter({
        label: m.packaging_search_label,
        placeholder: m.packaging_search_placeholder,
    }),
})

/**
 * Kiểu khai tường minh: gộp hai `validateSearch` lại thì TS mất index signature
 * nên `q` / `bom_q` không còn nhìn thấy được ở call site.
 */
type PackagingSearch = {
    q?: string
    bom_q?: string
    page?: number
    tab: PackagingTab
}

export const Route = createFileRoute('/_app/_authed/_sidebar/packaging/')({
    component: PackagingPage,
    validateSearch: (search: Record<string, unknown>): PackagingSearch => ({
        ...(MATERIALS_FILTERS.validateSearch(search) as {
            q?: string
            page?: number
        }),
        ...(BOM_FILTERS.validateSearch(search) as { bom_q?: string }),
        // `tab` là trạng thái điều hướng, không phải bộ lọc.
        tab: search.tab === 'bom' ? 'bom' : 'materials',
    }),
})

function PackagingPage() {
    const { hasPermission, arePermissionsLoaded } = useAuth()
    if (arePermissionsLoaded && !hasPermission(PERMISSION)) {
        return <ForbiddenState />
    }
    return <PackagingContent />
}

function PackagingContent() {
    const navigate = useNavigate({ from: '/packaging/' })
    const { tab } = Route.useSearch()
    // replace: đổi tab không nhồi thêm history. Giữ nguyên bộ lọc của cả hai tab.
    const setTab = (t: PackagingTab) =>
        void navigate({
            search: (prev) => ({ ...prev, tab: t }),
            replace: true,
        })

    return (
        <div className="space-y-4 page-pad">
            <div className="flex items-center gap-2">
                <PageBackButton className="-my-2 md:-my-1" />
                <Boxes className="size-6 text-primary" />
                <div>
                    <h1 className="text-2xl font-semibold">
                        {m.packaging_page_title()}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        {m.packaging_page_subtitle()}
                    </p>
                </div>
            </div>

            <Tabs
                value={tab}
                onValueChange={(v) => setTab(v as PackagingTab)}
                className="space-y-4"
            >
                <TabsList className="w-fit self-center">
                    <TabsTrigger value="materials">
                        {m.packaging_tab_materials()}
                    </TabsTrigger>
                    <TabsTrigger value="bom">
                        {m.packaging_tab_bom()}
                    </TabsTrigger>
                </TabsList>
                <TabsContent value="materials">
                    <MaterialsTab />
                </TabsContent>
                <TabsContent value="bom">
                    <BOMTab />
                </TabsContent>
            </Tabs>
        </div>
    )
}

function MaterialsTab() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('packaging.write')
    const navigate = useNavigate({ from: '/packaging/' })
    const { values, barProps, page, setPage } = useListFilters({
        def: MATERIALS_FILTERS,
        search: Route.useSearch(),
        onSearchChange: (next, opts) =>
            void navigate({
                // Giữ khoá của tab kia và `tab`: `toSearch` chỉ biết field của
                // config này.
                search: (prev) => ({
                    ...next,
                    bom_q: prev.bom_q,
                    tab: prev.tab,
                }),
                replace: opts?.replace ?? false,
            }),
    })

    const [createOpen, setCreateOpen] = useState(false)
    const [editing, setEditing] = useState<PackagingMaterial | null>(null)
    const deleteMutation = useDeletePackagingMaterial()

    // Map bộ lọc → tham số API. Để tường minh ở màn: mỗi màn map một kiểu.
    const params = useMemo<ListPackagingMaterialsParams>(
        () => ({
            page,
            per_page: 50,
            ...(values.q ? { search: values.q } : {}),
        }),
        [page, values.q],
    )
    const { data, isLoading } = usePackagingMaterials(params)
    const items = data?.items ?? []
    const total = data?.meta?.total ?? 0
    const perPage = data?.meta?.per_page ?? 50
    const currentPage = data?.meta?.page ?? page

    // Rỗng vì chưa có dữ liệu khác hẳn rỗng vì bộ lọc quá hẹp — trường hợp thứ
    // hai phải cho người dùng lối thoát ngay tại chỗ (R10).
    const emptyState = (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
            <span className="text-muted-foreground">
                {values.q
                    ? m.list_empty_filtered()
                    : m.packaging_material_table_empty()}
            </span>
            {values.q ? (
                <ClearFilterButton active onClear={barProps.onClear} />
            ) : null}
        </div>
    )

    const handleDelete = (mat: PackagingMaterial) => {
        if (
            !window.confirm(
                m.packaging_material_delete_confirm({ name: mat.name }),
            )
        )
            return
        deleteMutation.mutate(mat.id)
    }

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0 max-w-md flex-1">
                    <ListFilterBar {...barProps} />
                </div>
                {canWrite ? (
                    <Button onClick={() => setCreateOpen(true)}>
                        <Plus className="size-4 mr-1" />
                        {m.packaging_material_create()}
                    </Button>
                ) : null}
            </div>

            {/* Mobile: card list */}
            <div className="space-y-2 md:hidden">
                {isLoading ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.common_loading()}
                    </div>
                ) : items.length === 0 ? (
                    emptyState
                ) : (
                    items.map((mat) => (
                        <ListCard
                            key={mat.id}
                            title={
                                <span className="font-medium break-words">
                                    {mat.name}
                                </span>
                            }
                            subtitle={mat.sku}
                            badge={
                                mat.is_active ? (
                                    <Badge className="bg-green-100 text-green-700">
                                        {m.packaging_material_active()}
                                    </Badge>
                                ) : (
                                    <Badge variant="outline">
                                        {m.packaging_material_status_inactive()}
                                    </Badge>
                                )
                            }
                            actions={
                                canWrite ? (
                                    <>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            aria-label={m.common_edit()}
                                            onClick={() => setEditing(mat)}
                                        >
                                            <Edit className="size-4" />
                                        </Button>
                                        {mat.is_active ? (
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                aria-label={m.common_delete()}
                                                onClick={() =>
                                                    handleDelete(mat)
                                                }
                                                disabled={
                                                    deleteMutation.isPending
                                                }
                                            >
                                                <Trash2 className="size-4 text-red-500" />
                                            </Button>
                                        ) : null}
                                    </>
                                ) : null
                            }
                            fields={[
                                {
                                    label: m.packaging_material_unit(),
                                    value: mat.unit,
                                },
                                {
                                    label: m.packaging_material_min_stock(),
                                    value: String(mat.min_stock),
                                },
                                {
                                    label: m.packaging_material_tracking_mode_col(),
                                    value:
                                        mat.tracking_mode === 'quantity'
                                            ? m.packaging_material_tracking_mode_quantity()
                                            : m.packaging_material_tracking_mode_lot(),
                                },
                                {
                                    label: m.packaging_material_barcode(),
                                    value: mat.barcode ?? '—',
                                },
                            ]}
                        />
                    ))
                )}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.packaging_material_sku()}</TableHead>
                            <TableHead>{m.packaging_material_name()}</TableHead>
                            <TableHead>{m.packaging_material_unit()}</TableHead>
                            <TableHead>
                                {m.packaging_material_tracking_mode_col()}
                            </TableHead>
                            <TableHead className="text-right">
                                {m.packaging_material_min_stock()}
                            </TableHead>
                            <TableHead>
                                {m.packaging_material_barcode()}
                            </TableHead>
                            <TableHead>
                                {m.packaging_material_active()}
                            </TableHead>
                            {canWrite ? (
                                <TableHead className="text-right">
                                    {m.packaging_bom_actions()}
                                </TableHead>
                            ) : null}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={canWrite ? 8 : 7}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.common_loading()}
                                </TableCell>
                            </TableRow>
                        ) : items.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={canWrite ? 8 : 7}>
                                    {emptyState}
                                </TableCell>
                            </TableRow>
                        ) : (
                            items.map((mat) => (
                                <TableRow key={mat.id}>
                                    <TableCell className="font-mono text-sm">
                                        {mat.sku}
                                    </TableCell>
                                    <TableCell className="whitespace-normal">
                                        {mat.name}
                                    </TableCell>
                                    <TableCell>{mat.unit}</TableCell>
                                    <TableCell>
                                        {mat.tracking_mode === 'quantity' ? (
                                            <Badge
                                                variant="outline"
                                                className="border-blue-300 text-blue-700 bg-blue-50"
                                            >
                                                {m.packaging_material_tracking_mode_quantity()}
                                            </Badge>
                                        ) : (
                                            <Badge
                                                variant="outline"
                                                className="border-slate-300 text-slate-700"
                                            >
                                                {m.packaging_material_tracking_mode_lot()}
                                            </Badge>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {mat.min_stock}
                                    </TableCell>
                                    <TableCell className="font-mono text-xs">
                                        {mat.barcode ?? '—'}
                                    </TableCell>
                                    <TableCell>
                                        {mat.is_active ? (
                                            <Badge className="bg-green-100 text-green-700">
                                                ✓
                                            </Badge>
                                        ) : (
                                            <Badge variant="outline">
                                                {m.packaging_material_status_inactive()}
                                            </Badge>
                                        )}
                                    </TableCell>
                                    {canWrite ? (
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() =>
                                                        setEditing(mat)
                                                    }
                                                >
                                                    <Edit className="size-4" />
                                                </Button>
                                                {mat.is_active ? (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() =>
                                                            handleDelete(mat)
                                                        }
                                                        disabled={
                                                            deleteMutation.isPending
                                                        }
                                                    >
                                                        <Trash2 className="size-4 text-red-500" />
                                                    </Button>
                                                ) : null}
                                            </div>
                                        </TableCell>
                                    ) : null}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            <div className="flex items-center justify-between">
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                >
                    {m.common_prev_page()}
                </Button>
                <div className="text-sm text-muted-foreground">
                    {m.common_page()} {currentPage}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={!total || currentPage * perPage >= total}
                    onClick={() => setPage(page + 1)}
                >
                    {m.common_next_page()}
                </Button>
            </div>

            <MaterialFormDialog
                open={createOpen}
                onOpenChange={setCreateOpen}
                material={null}
            />
            <MaterialFormDialog
                open={Boolean(editing)}
                onOpenChange={(v) => !v && setEditing(null)}
                material={editing}
            />
        </div>
    )
}

function BOMTab() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('packaging.write')
    const navigate = useNavigate({ from: '/packaging/' })
    const { values, barProps, page, setPage } = useListFilters({
        def: BOM_FILTERS,
        search: Route.useSearch(),
        onSearchChange: (next, opts) =>
            void navigate({
                // Giữ khoá của tab kia và `tab`.
                search: (prev) => ({ ...next, q: prev.q, tab: prev.tab }),
                replace: opts?.replace ?? false,
            }),
    })

    const [editing, setEditing] = useState<{
        id: string
        name: string
    } | null>(null)

    // Map bộ lọc → tham số API. Để tường minh ở màn: mỗi màn map một kiểu.
    const { data: productsData, isLoading } = useProducts({
        ...(values.bom_q ? { search: values.bom_q } : {}),
        page,
        per_page: BOM_PER_PAGE,
    })
    const products = useMemo(() => productsData?.data ?? [], [productsData])
    const total = productsData?.meta?.total ?? 0
    const currentPage = productsData?.meta?.page ?? page
    const productIds = useMemo(() => products.map((p) => p.id), [products])
    const { data: counts = {} } = useBOMCounts(productIds)

    return (
        <div className="space-y-4">
            <div className="max-w-md">
                <ListFilterBar {...barProps} />
            </div>

            <div className="rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.packaging_bom_sku()}</TableHead>
                            <TableHead>{m.packaging_bom_product()}</TableHead>
                            <TableHead className="text-right">
                                {m.packaging_bom_count()}
                            </TableHead>
                            <TableHead className="text-right">
                                {m.packaging_bom_actions()}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={4}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.common_loading()}
                                </TableCell>
                            </TableRow>
                        ) : products.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={4}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.packaging_material_table_empty()}
                                </TableCell>
                            </TableRow>
                        ) : (
                            products.map((p) => {
                                const summary = counts[p.id]
                                return (
                                    <TableRow key={p.id}>
                                        <TableCell className="font-mono text-sm">
                                            {p.sku}
                                        </TableCell>
                                        <TableCell className="whitespace-normal">
                                            {p.name}
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums">
                                            {summary?.count ?? 0}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() =>
                                                    setEditing({
                                                        id: p.id,
                                                        name: p.name,
                                                    })
                                                }
                                                disabled={!canWrite}
                                            >
                                                <Edit className="size-4 mr-1" />
                                                {m.packaging_bom_edit()}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                )
                            })
                        )}
                    </TableBody>
                </Table>
            </div>

            <div className="flex items-center justify-between">
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                >
                    {m.common_prev_page()}
                </Button>
                <div className="text-sm text-muted-foreground">
                    {m.common_page()} {currentPage}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={!total || currentPage * BOM_PER_PAGE >= total}
                    onClick={() => setPage(page + 1)}
                >
                    {m.common_next_page()}
                </Button>
            </div>

            {editing ? (
                <BOMEditDialog
                    open={Boolean(editing)}
                    onOpenChange={(v) => !v && setEditing(null)}
                    productId={editing.id}
                    productName={editing.name}
                />
            ) : null}
        </div>
    )
}

function ForbiddenState() {
    return (
        <Card>
            <CardContent className="py-12 text-center space-y-2">
                <h2 className="text-lg font-semibold">
                    {m.packaging_forbidden_title()}
                </h2>
                <p className="text-sm text-muted-foreground">
                    {m.packaging_forbidden_desc()}
                </p>
            </CardContent>
        </Card>
    )
}
